import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { LogOut, Trash2, Download, ArrowUpRight, Mail, Gift, Sun, Moon, BookOpen, MessageSquare } from 'lucide-react'
import { FaDiscord } from 'react-icons/fa'
import toast from 'react-hot-toast'
import { useTheme } from '../contexts/ThemeContext'
import { UsageDisplay } from '../components/UsageDisplay'

export default function SettingsPage() {
  const navigate = useNavigate()
  const { user, logout, setUser, token } = useAuthStore()
  const { theme, setTheme } = useTheme()
  const [isLoading, setIsLoading] = useState(false)
  const [name, setName] = useState(user?.name || '')
  const [isEditing, setIsEditing] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showLiveChatModal, setShowLiveChatModal] = useState(false)

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const handleExportData = async () => {
    if (!token) {
      toast.error('Please log in to export')
      return
    }

    setIsLoading(true)

    try {
      const response = await fetch('/api/export/obsidian', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) throw new Error('Export failed')

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const date = new Date().toISOString().split('T')[0]
      a.download = `Memtrail-export-${date}.zip`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      // Silent success - file downloads automatically
    } catch (error) {
      console.error('Export error:', error)
      toast.error('Export failed')
    } finally {
      setIsLoading(false)
    }
  }


  const handleDeleteAccount = async () => {
    setIsLoading(true)
    try {
      // TODO: Implement account deletion API
      toast.success('Account deletion request submitted')
      setShowDeleteConfirm(false)
    } catch (error) {
      toast.error('Account deletion failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveName = () => {
    if (!user) return
    setUser({ ...user, name })
    setIsEditing(false)
    toast.success('Name updated!')
  }

  const handleDiscordClick = () => {
    // Open Discord invite link - replace with your actual Discord server invite
    window.open('https://discord.gg/Memtrail', '_blank')
  }

  const handleLiveChatClick = () => {
    // Show modal explaining live chat is coming soon or open chat widget
    setShowLiveChatModal(true)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">Settings</h1>
        </div>

        {/* Usage Section */}
        <div className="mb-6">
          <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 px-1">
            Usage & Limits
          </h2>
          <UsageDisplay />
        </div>

        {/* Account Section */}
        <div className="mb-6">
          <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 px-1">
            Account
          </h2>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                <span className="text-xl font-semibold text-white">
                  {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                {!isEditing ? (
                  <>
                    <p className="text-base font-medium text-gray-900 dark:text-white truncate">
                      {user?.name || 'Add your name'}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                      {user?.email}
                    </p>
                  </>
                ) : (
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your name"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    autoFocus
                  />
                )}
              </div>
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="text-sm text-blue-600 dark:text-blue-400 font-medium hover:text-blue-700"
                >
                  Edit
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveName}
                    className="text-sm text-blue-600 dark:text-blue-400 font-medium hover:text-blue-700"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => { setIsEditing(false); setName(user?.name || '') }}
                    className="text-sm text-gray-500 dark:text-gray-400 font-medium hover:text-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>


        {/* Preferences Section */}
        <div className="mb-6">
          <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 px-1">
            Preferences
          </h2>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {theme === 'dark' ? (
                  <Moon className="w-5 h-5 text-gray-400" />
                ) : (
                  <Sun className="w-5 h-5 text-gray-400" />
                )}
                <span className="text-sm font-medium text-gray-900 dark:text-white">Appearance</span>
              </div>
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setTheme('light')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    theme === 'light'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Light
                </button>
                <button
                  onClick={() => setTheme('dark')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    theme === 'dark'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  Dark
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Data Section */}
        <div className="mb-6">
          <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 px-1">
            Data
          </h2>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={handleExportData}
              disabled={isLoading}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors disabled:opacity-50"
            >
              <div className="flex items-center gap-3">
                <Download className="w-5 h-5 text-gray-400" />
                <div className="text-left">
                  <span className="text-sm font-medium text-gray-900 dark:text-white block">Export my data</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Download all articles and trails as markdown</span>
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>


        {/* Help & Support Section */}
        <div className="mb-6">
          <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 px-1">
            Help & Support
          </h2>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
            <button
              onClick={() => navigate('/documentation')}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">How to use Memtrail</span>
              </div>
              <ArrowUpRight className="w-4 h-4 text-gray-400" />
            </button>

            <button
              onClick={() => navigate('/invite')}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Gift className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">Invite a friend</span>
              </div>
              <ArrowUpRight className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Contact Support Section */}
        <div className="mb-6">
          <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 px-1">
            Contact Support
          </h2>
          
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
            <a
              href="mailto:support@memtrail.app"
              className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <div className="text-left">
                  <span className="text-sm font-medium text-gray-900 dark:text-white block">Email Support</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Get help via email</span>
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-gray-400" />
            </a>

            <button
              onClick={handleDiscordClick}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <FaDiscord className="w-5 h-5 text-gray-400" />
                <div className="text-left">
                  <span className="text-sm font-medium text-gray-900 dark:text-white block">Discord Community</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Join our community</span>
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-gray-400" />
            </button>

            <button
              onClick={handleLiveChatClick}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-gray-400" />
                <div className="text-left">
                  <span className="text-sm font-medium text-gray-900 dark:text-white block">Live Chat</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Chat with support team</span>
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Account Actions Section */}
        <div className="mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden divide-y divide-gray-200 dark:divide-gray-700">
            <button
              onClick={handleLogout}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <LogOut className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">Sign out</span>
              </div>
            </button>

            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full p-4 flex items-center justify-between hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Trash2 className="w-5 h-5 text-red-500" />
                <span className="text-sm font-medium text-red-600 dark:text-red-400">Delete account</span>
              </div>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-xs text-gray-400 dark:text-gray-500">Memtrail v1.0.0</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Made with ♥ for curious minds</p>
        </div>
      </div>


      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-sm w-full p-6">
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Delete account?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This will permanently delete your account and all your data. This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isLoading}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Live Chat Modal */}
      {showLiveChatModal && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowLiveChatModal(false)}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Live Chat Support
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Our live chat is currently being set up. In the meantime, you can reach us through:
              </p>
            </div>

            <div className="space-y-3 mb-6">
              <a
                href="mailto:support@memtrail.app"
                className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <Mail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <div className="text-left flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">Email</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">support@memtrail.app</div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-gray-400" />
              </a>

              <button
                onClick={() => {
                  setShowLiveChatModal(false)
                  handleDiscordClick()
                }}
                className="w-full flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <FaDiscord className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <div className="text-left flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">Discord</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Join our community</div>
                </div>
                <ArrowUpRight className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <button
              onClick={() => setShowLiveChatModal(false)}
              className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

    </div>
  )
}
